import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownGroup } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { isJapaneseLocale } from '@helpers/utils/locale/locale';
import { mapNavigationKeyToPathname } from '@terminal/components/app-header/utils/navigation-utils';
import clsx from 'clsx';
import { useState } from 'react';

import { HeaderCategoryItem } from '../category-group/HeaderCategoryGroup';
import { AlfredNavItem } from './AlfredNavItem';
import styles from './NavItem.module.scss';

export type NavItemProps = {
  item: HeaderCategoryItem;
  activeKey?: string;
  disabled?: boolean;
  showBottomBorder?: boolean;
};

export const NavItem = ({ item, activeKey, disabled }: NavItemProps) => {
  const isActive = activeKey === item.key;
  const isJapanese = isJapaneseLocale();
  const [isCustomDropdownOpen, setIsCustomDropdownOpen] = useState(false);
  const handleCloseCustomDropdown = () => setIsCustomDropdownOpen(false);

  const customDropdownContent =
    typeof item.customDropdownContent === 'function'
      ? item.customDropdownContent(handleCloseCustomDropdown)
      : item.customDropdownContent;

  if (item.isAlfred) {
    return (
      <Tooltip
        label={item.label}
        disabled={!item.showTooltip}
        trigger={TooltipTrigger.Hover}
        placement="bottom"
      >
        <div className={styles.itemWrapper} key={item.key}>
          <AlfredNavItem
            item={item}
            disabled={disabled}
            isActive={isActive}
            isLoading={item.isProcessing}
            isDefault={!item.isProcessing}
            isFinished={item.isFinished}
            unreadConversations={item.unreadConversations}
            onConversationClick={item.onConversationClick}
          />
          {item.notification && (
            <span
              className={styles.notificationWrapper}
              style={{ right: 0, top: -5 }}
            >
              {item.notification}
            </span>
          )}
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label={item.label}
      disabled={!item.showTooltip}
      trigger={TooltipTrigger.Hover}
      placement="bottom"
    >
      <div className={styles.itemWrapper} key={item.key}>
        {customDropdownContent ? (
          <DropdownMenu
            className={styles.styledCustomDropdown}
            key={item.key}
            align="end"
            modal={false}
            open={isCustomDropdownOpen}
            onOpenChange={setIsCustomDropdownOpen}
            triggerNode={
              <button
                className={clsx(styles.buttonItem, {
                  [styles.disabled]: disabled,
                })}
                data-testid={`header-navigation-item-${item.key}`}
              >
                <div className={styles.itemLeft}>
                  <span className={styles.iconWrapper}>{item.icon}</span>
                </div>
              </button>
            }
          >
            {customDropdownContent}
          </DropdownMenu>
        ) : item.dropdownItems?.length ? (
          <DropdownMenu
            className={styles.styledDropdown}
            key={item.key}
            triggerNode={
              <button
                className={styles.buttonItem}
                data-testid={`header-navigation-item-${item.key}`}
              >
                <div className={styles.itemLeft}>
                  <span className={styles.iconWrapper}>{item.icon}</span>
                </div>
              </button>
            }
          >
            {item.dropdownItems.map((group, key) => (
              <DropdownGroup items={group} key={key} />
            ))}
          </DropdownMenu>
        ) : (
          <a
            className={clsx(styles.buttonItem, {
              [styles.isActive]: isActive,
              [styles.pillStyle]: !!item.pillStyle,
              [styles.isAlfred]: item.isAlfred,
              [styles.isJapanese]: isJapanese,
              [styles.disabled]: disabled,
            })}
            href={mapNavigationKeyToPathname[item.key]}
            data-testid={`header-navigation-item-${item.key}`}
            aria-disabled={disabled || undefined}
            onClick={e => {
              e.preventDefault();

              if (disabled) {
                return;
              }

              item.onClick(item, e);
            }}
          >
            <div className={styles.itemLeft}>
              {item.icon && (
                <span className={styles.iconWrapper}>{item.icon}</span>
              )}
              {item.label && !item.showTooltip && (
                <span
                  className={clsx(styles.mainLabel, {
                    [styles.isAlfred]: item.isAlfred,
                    [styles.isJapanese]: isJapanese,
                  })}
                >
                  {item.label}
                </span>
              )}
            </div>
          </a>
        )}
        {item.notification && (
          <span
            className={styles.notificationWrapper}
            style={{ right: 0, top: -5 }}
          >
            {item.notification}
          </span>
        )}
      </div>
    </Tooltip>
  );
};
